/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import { oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import {
  propTypes,
  LISTING_UNIT_TYPES,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
} from '../../util/types';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemProviderStripeCommissionMaybe from './LineItemProviderStripeCommissionMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';

import css from './OrderBreakdown.module.css';
import Decimal from 'decimal.js';
import { types as sdkTypes } from '../../util/sdkLoader';
import { transitions } from '../../transactions/transactionProcessBooking';
const { Money } = sdkTypes;
const PERCENTS = 100;
const STRIPE_FEE = 2.9;
const ADDITIONAL_STRIPE_FEE = 30;
const PROVIDERS_COMMISSION = 5;
const CUSTOMERS_COMMISSION = 20;

const calculateStripeFee = payinTotal => {
  const fee = (payinTotal * STRIPE_FEE) / PERCENTS + ADDITIONAL_STRIPE_FEE;
  return fee.toFixed();
};

export const calculateAmountIfCustomerDispute = (itemPrice, partialRefundAmount, stripeFee) => {
  //   Refund to Athlete (customer) (100*75%)

  //   Creator (provider) received (100*(100%-75%)*10%)



  const priceAfterRefund = partialRefundAmount > itemPrice ? 0 : itemPrice - partialRefundAmount;


  const initialCustomerCommission = (itemPrice * CUSTOMERS_COMMISSION) / PERCENTS;
  const customerCommissionAfterRefund = (priceAfterRefund * CUSTOMERS_COMMISSION) / PERCENTS;
  const customerCommissionToRefund = initialCustomerCommission - customerCommissionAfterRefund;
  const totalForCustomer = priceAfterRefund + customerCommissionAfterRefund;




  const initialProviderCommission = (itemPrice * PROVIDERS_COMMISSION) / PERCENTS;

  const providerCommissionAfterRefund = (priceAfterRefund * PROVIDERS_COMMISSION) / PERCENTS;
  const providerCommissionToRefund = initialProviderCommission - providerCommissionAfterRefund;
  const totalForProvider = priceAfterRefund + providerCommissionAfterRefund;



  return {
    priceAfterRefund,
    provider: {
      initialProviderCommission,
      providerCommissionAfterRefund,
      providerCommissionToRefund,
      totalForProvider
    },
    customer: {
      initialCustomerCommission,
      customerCommissionAfterRefund,
      customerCommissionToRefund,
      totalForCustomer
    }

  };
};



export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    intl,
    dateType,
    timeZone,
    currency,
    marketplaceName,
  } = props;


  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const allLineItems = transaction.attributes.lineItems || [];

  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  let lineItems = allLineItems.filter(lineItem => lineItem.includeFor.includes(userRole));
  const isPartialRefund = !!transaction?.attributes?.metadata?.partialRefundData || false;
  const serviceLineItem = lineItems.filter(lineItem => lineItem.code === 'line-item/night' || lineItem.code === 'line-item/day' || lineItem.code === 'line-item/hour')
  // const servicePrice = serviceLineItem.lineTotal.amount;
  const serviceLineItems = lineItems?.filter(
    l => l.code !== 'line-item/night' && l.code !== 'line-item/day' && l.code !== 'line-item/hour' && l.code !== 'line-item/item' && l.code !== 'line-item/customer-commission' && l.code !== 'line-item/provider-commission'
  );

  const withoutServiceLineItems = lineItems?.filter(
    l => l.code === 'line-item/night' || l.code === 'line-item/day' || l.code === 'line-item/hour' || l.code === 'line-item/item' || l.code === 'line-item/customer-commission' || l.code === 'line-item/provider-commission'
  );
  const servicePriceArray = serviceLineItems.map(item => item.lineTotal.amount);
  let servicePriceSum = servicePriceArray.reduce((partialSum, a) => partialSum + a, 0);

  const isProviderCancel = transaction.attributes.lastTransition === transitions.PROVIDER_CANCEL;
  const isCustomerCancel = transaction.attributes.lastTransition === transitions.CUSTOMER_CANCEL;
  const isCancel = isCustomerCancel || isProviderCancel;
  const isProviderPaysStripeFee =
    transaction.attributes.lastTransition === transitions.PROVIDER_CANCEL ||
    transaction.attributes.lastTransition === transitions.ACCEPT_CUSTOMER_DISPUTE ||
    transaction.attributes.lastTransition === transitions.DECLINE_PROVIDER_DISPUTE;
  const payinTotal = transaction?.attributes?.payinTotal?.amount;
  const stripeFee = calculateStripeFee(payinTotal);
  const itemPrice = transaction.attributes.lineItems.find(
    lineItem =>
      lineItem.code === 'line-item/night' ||
      lineItem.code === 'line-item/day' ||
      lineItem.code === 'line-item/hour' ||
      lineItem.code === 'line-item/item'
  );
  let totalForProvider = 0, totalForCustomer = 0;


  const stripeFeeLineItem =  isProviderPaysStripeFee ? [{
    code: 'line-item/stripe-fee',
    includeFor: ['provider'],
    quantity: new Decimal(1),
    unitPrice: new Decimal(1),
    lineTotal: new Money(-stripeFee, 'USD'),
    reversal: false,
  }] : [];



  if(isPartialRefund) {
    const partialRefundAmount = transaction.attributes.metadata.partialRefundData.amount;
    const customLineItems = calculateAmountIfCustomerDispute(servicePriceSum, partialRefundAmount, stripeFee);


    const {
      provider: { providerCommissionToRefund },
      customer: { customerCommissionToRefund },
    } = customLineItems;
    totalForCustomer = customLineItems?.customer?.totalForCustomer;
    totalForProvider = servicePriceSum === partialRefundAmount ? stripeFee : customLineItems?.provider?.totalForProvider;
    const currentRefundAmount = partialRefundAmount > servicePriceSum ? servicePriceSum : partialRefundAmount;


    const partialRefundLineItems = withoutServiceLineItems.map(lineItem => {
      if(lineItem.code === 'line-item/night' || lineItem.code === 'line-item/day' || lineItem.code === 'line-item/hour' || lineItem.code === 'line-item/item') {
        return   {
          code: lineItem.code,
          includeFor: lineItem.includeFor,
          quantity: lineItem.quantity,
          unitPrice: lineItem.unitPrice,
          lineTotal: isCancel ? new Money(-servicePriceSum, 'USD') : new Money(-currentRefundAmount, 'USD'),
          reversal: true,
        }
      }
      if(lineItem.code === 'line-item/customer-commission') {
        return   {
          code: lineItem.code,
          includeFor: lineItem.includeFor,
          percentage: lineItem.percentage,
          unitPrice: lineItem.unitPrice,
          lineTotal: isCustomerCancel ? new Money(0, 'USD') : isProviderCancel ? new Money(-lineItem.lineTotal.amount, 'USD') : new Money(-customerCommissionToRefund, 'USD'),
          reversal: true,
        }
      }
      if(lineItem.code === 'line-item/provider-commission') {
        return   {
          code: lineItem.code,
          includeFor: lineItem.includeFor,
          percentage: lineItem.percentage,
          unitPrice: lineItem.unitPrice,
          lineTotal: isCancel ? new Money(-lineItem?.lineTotal?.amount, 'USD') :  new Money(providerCommissionToRefund, 'USD'),
          reversal: true,
        }
      }
      else {
        return   {
          code: lineItem.code,
          includeFor: lineItem.includeFor,
          percentage: lineItem.percentage,
          unitPrice: lineItem.unitPrice,
          lineTotal: lineItem.lineTotal,
          reversal: true,
        }
      }
    });


    lineItems = [...lineItems, ...partialRefundLineItems, ...stripeFeeLineItem];
  }



  const unitLineItem = lineItems.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItem?.code;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const commissionLineItem = lineItems.filter(item => {
    const hasCustomerCommission = isCustomer && !item.reversal && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && !item.reversal && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return hasCustomerCommission || hasProviderCommission;
  });

  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  return (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
      />

      {/*<LineItemBasePriceMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />*/}
      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      <LineItemSubTotalMaybe
        lineItems={lineItems}
        code={lineItemUnitType}
        userRole={userRole}
        intl={intl}
        marketplaceCurrency={currency}
      />
      <LineItemRefundMaybe lineItems={lineItems} intl={intl} marketplaceCurrency={currency} />

      <LineItemCustomerCommissionMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderCommissionRefundMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderStripeCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemTotalPrice
        totalForProvider={totalForProvider}
        totalForCustomer={totalForCustomer}
        isProviderCancel={isProviderCancel}
        isCustomerCancel={isCustomerCancel}
        commissionLineItem={commissionLineItem}
        transaction={transaction}
        isProvider={isProvider}
        intl={intl}
        lineItems={lineItems}
      />

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}
    </div>
  );
};

OrderBreakdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  booking: null,
  dateType: null,
};

OrderBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  booking: propTypes.booking,
  dateType: propTypes.dateType,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OrderBreakdown = injectIntl(OrderBreakdownComponent);

OrderBreakdown.displayName = 'OrderBreakdown';

export default OrderBreakdown;
